import { useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/pro-regular-svg-icons";
import { faEllipsis } from "@fortawesome/pro-light-svg-icons";
import { ThemeProvider } from "styled-components";
import { Tooltip } from "react-tooltip";
import { DateTime } from "luxon";

import useNavigate from "core/hooks/useNavigate";
import Popover from "core/components/Popover";
import Table, {
  SearchableTableCell,
  TextWithIconTableCell,
} from "core/components/Table";
import Pill from "core/components/Pill";
import Link from "core/components/Link";
import {
  DIRECT_DATA_ACCESS_STATUS_LABEL,
  REVERSE_LOOKUP_ROLES,
  ROLES,
} from "core/constants";
import theme from "theme";
import Typography from "core/components/Typography";
import { getLastActiveCredential } from "./utilities";
import RemoveMemberConfirmModal from "./RemoveMemberConfirmModal";

const columnHelper = createColumnHelper();

const memberDataAccessTooltipId = "memberDataAccessTooltip";

const MemberDataAccessTooltip = ({ data }) => {
  return (
    <ThemeProvider theme={theme}>
      <div
        style={{
          padding: "8px 11px",
        }}
      >
        <Typography variant="sub-text" color="white" noMargin>
          Credential generated on{" "}
          {DateTime.fromMillis(data.generated_timestamp).toFormat("D")}
        </Typography>
      </div>
    </ThemeProvider>
  );
};

const staticColumns = [
  columnHelper.accessor("name", {
    header: "Full Name",
    sortingFn: "textCaseSensitive",
    size: 150,
    customStyles: { flexGrow: 3 },
    cell: (props) => {
      return (
        <TextWithIconTableCell
          position="left"
          icon={faUser}
          primaryColor="#0097C0"
        >
          {/* To-Do: Separate ticket - Clicking the name should open the user-profile panel on the right side */}
          <Link
            to={`/organization/${props.row.original.organization_id}/members/${props.row.original.userId}`}
            style={{
              fontSize: "14px",
              flexGrow: 1,
              overflowX: "clip",
              textOverflow: "ellipsis",
            }}
            // state={{
            //   from: "Members",
            //   userEmail: props.row.original.email,
            //   orgId: props.row.original.organizationId,
            // }}
          >
            <SearchableTableCell
              searchConfig={props.table.options.meta.searchConfig}
              columnId={props.column.id}
              placeholder="Missing name"
            >
              {props.getValue()}
            </SearchableTableCell>
          </Link>
        </TextWithIconTableCell>
      );
    },
  }),
  columnHelper.accessor("email", {
    header: "Email",
    size: 200,
    customStyles: { flexGrow: 3 },
    cell: SearchableTableCell,
  }),
  columnHelper.accessor((row) => ROLES[row.role], {
    id: "role",
    header: "Role",
    size: 100,
    customStyles: { flexGrow: 1 },
  }),
  columnHelper.accessor("direct_data_access_enabled", {
    id: "directDataAccess",
    header: "Data Access",
    size: 110,
    customStyles: { flexGrow: 1 },
    cell: (props) => {
      const data_access_info = props.row.original.data_access_info;
      const mostRecentCredential = data_access_info
        ? getLastActiveCredential(data_access_info)
        : null;

      const text = props.getValue()
        ? DIRECT_DATA_ACCESS_STATUS_LABEL.ENABLED
        : DIRECT_DATA_ACCESS_STATUS_LABEL.DISABLED;

      const variant = props.getValue() ? "success" : "default";

      return (
        <Pill
          text={text}
          size="small"
          variant={variant}
          tooltipId={memberDataAccessTooltipId}
          tooltipContent={
            props.getValue() && mostRecentCredential ? (
              <MemberDataAccessTooltip data={{ ...mostRecentCredential }} />
            ) : (
              ""
            )
          }
        />
      );
    },
  }),
];

const MemberTable = (props) => {
  const {
    data,
    isRefreshing,
    searchFilter = { searchValue: "", columns: [] },
    isLoading,
    canUpdate,
    userRole,
    style,
  } = props;

  const navigate = useNavigate();
  const [memberToRemove, setMemberToRemove] = useState(null);

  const dynamicColumns = [
    columnHelper.display({
      id: "actions",
      size: 50,
      customStyles: {
        flexGrow: 0,
        display: "flex",
        justifyContent: "flex-end",
      },
      cell: ({ row }) => {
        const openRowMenuId = row.original.userId;
        const openRowMenuMember = data.find(
          (user) => user.userId === openRowMenuId
        );
        const enableMemberUpdate =
          userRole === ROLES.systemAdmin ||
          [
            REVERSE_LOOKUP_ROLES[ROLES.orgAdmin],
            REVERSE_LOOKUP_ROLES[ROLES.orgUser],
          ].includes(openRowMenuMember.role);

        const actions = [
          {
            name: "Edit",
            dataTest: "editMember",
            onClick: () =>
              navigate(
                `/organization/${openRowMenuMember.organization_id}/members/${openRowMenuMember.user_id}`
              ),
          },
          {
            name: "Remove",
            dataTest: "removeMember",
            onClick: () => setMemberToRemove(openRowMenuMember),
          },
        ];

        return enableMemberUpdate ? (
          <Popover
            placement="bottom-end"
            offset={3}
            actions={actions}
            TriggerComponent={(props) => {
              const { setRef, email, name, isNavOpen, ...triggerProps } = props;

              return (
                <div
                  style={{
                    width: "50px",
                  }}
                >
                  <button
                    type="button"
                    data-test="editMenu"
                    aria-label={`Row actions for ${
                      openRowMenuMember.name || openRowMenuMember.email
                    }`}
                    style={{
                      width: "100%",
                      padding: 0,
                      border: "none",
                      background: "none",
                      color: "inherit",
                      lineHeight: 1,
                      fontSize: "19px",
                      cursor: enableMemberUpdate ? "pointer" : "default",
                      pointerEvents: enableMemberUpdate ? "auto" : "none",
                      opacity: enableMemberUpdate ? 1 : 0.5,
                    }}
                    ref={setRef}
                    {...triggerProps}
                  >
                    <FontAwesomeIcon icon={faEllipsis} />
                  </button>
                </div>
              );
            }}
          />
        ) : null;
      },
    }),
  ];

  const columnVisibility = useMemo(
    () => ({
      directDataAccess: true,
      actions: canUpdate,
    }),
    [canUpdate]
  );

  return (
    <>
      <Table
        data={data}
        columns={staticColumns.concat(dynamicColumns)}
        isRefreshing={isRefreshing}
        searchConfig={searchFilter}
        isLoading={isLoading}
        columnVisibility={columnVisibility}
        pageKey="Members"
        isVirtualized={true}
        virtualWindowHeightOffset={500}
        style={{ ...style }}
      />

      <Tooltip
        id={memberDataAccessTooltipId}
        style={{ zIndex: "10000" }}
        place={"bottom"}
      />

      {memberToRemove ? (
        <RemoveMemberConfirmModal
          member={memberToRemove}
          isOpen={!!memberToRemove}
          close={() => setMemberToRemove(null)}
        />
      ) : null}
    </>
  );
};

export default MemberTable;
